import { Option, Select } from '@d-edge/overnight-hotelier-react'
import type { WorkflowStep } from '@/api/types'
import { Button, IconButton } from '@/components/ui/Button'
import { Icons } from '@/components/ui/Icons'
import styles from './settings.module.css'

interface WorkflowEditorProps {
  value: WorkflowStep[]
  requesterTypes: string[]
  proxyProducts: string[]
  onChange: (steps: WorkflowStep[]) => void
}

/** Ordered requester steps, each trying its proxy products in turn with a retry count. */
export function WorkflowEditor({ value, requesterTypes, proxyProducts, onChange }: WorkflowEditorProps) {
  const updateStep = (index: number, patch: Partial<WorkflowStep>) => onChange(value.map((s, i) => (i === index ? { ...s, ...patch } : s)))
  const updateProxy = (index: number, proxyIndex: number, patch: Partial<WorkflowStep['proxyConfigurations'][number]>) =>
    updateStep(index, { proxyConfigurations: value[index]!.proxyConfigurations.map((p, i) => (i === proxyIndex ? { ...p, ...patch } : p)) })
  const move = (index: number, delta: number) => {
    const next = [...value]
    const target = index + delta
    if (target < 0 || target >= next.length) return
    const [step] = next.splice(index, 1)
    next.splice(target, 0, step!)
    onChange(next)
  }

  return (
    <div className={styles.workflow}>
      {value.map((step, index) => (
        <div key={index} className={styles.step}>
          <span className={styles.stepIndex}>{index + 1}</span>
          <Select fullWidth value={step.requesterType} onChange={(requesterType) => updateStep(index, { requesterType })} aria-label={`Requester of step ${index + 1}`}>
            {requesterTypes.map((r) => (
              <Option key={r} value={r} label={r} />
            ))}
          </Select>
          <div className={styles.proxies}>
            {step.proxyConfigurations.map((proxy, proxyIndex) => (
              <div key={proxyIndex} className={styles.proxyRow}>
                <Select fullWidth placeholder="– proxy product –" value={proxy.proxyProduct} onChange={(proxyProduct) => updateProxy(index, proxyIndex, { proxyProduct })} aria-label="Proxy product">
                  {proxyProducts.map((p) => (
                    <Option key={p} value={p} label={p} />
                  ))}
                </Select>
                <input
                  type="number"
                  min={0}
                  value={proxy.retries}
                  onChange={(e) => updateProxy(index, proxyIndex, { retries: Number(e.target.value) })}
                  aria-label="Retries"
                  title="Retries"
                  className="htl-text-input htl-text-input--full-width"
                />
                <IconButton label="Remove proxy product" onClick={() => updateStep(index, { proxyConfigurations: step.proxyConfigurations.filter((_, i) => i !== proxyIndex) })}>
                  <Icons.Close size={13} />
                </IconButton>
              </div>
            ))}
            <Button size="sm" variant="ghost" icon={<Icons.Plus size={12} />} onClick={() => updateStep(index, { proxyConfigurations: [...step.proxyConfigurations, { proxyProduct: proxyProducts[0] ?? '', retries: 1 }] })}>
              Proxy product
            </Button>
          </div>
          <span className="u-row">
            <IconButton label="Move up" disabled={index === 0} onClick={() => move(index, -1)}>
              <Icons.ArrowUp size={13} />
            </IconButton>
            <IconButton label="Move down" disabled={index === value.length - 1} onClick={() => move(index, 1)}>
              <Icons.ArrowDown size={13} />
            </IconButton>
            <IconButton label="Remove step" onClick={() => onChange(value.filter((_, i) => i !== index))}>
              <Icons.Trash size={13} />
            </IconButton>
          </span>
        </div>
      ))}
      <div>
        <Button size="sm" icon={<Icons.Plus size={12} />} onClick={() => onChange([...value, { requesterType: requesterTypes[0] ?? '', proxyConfigurations: [] }])}>
          Add step
        </Button>
      </div>
    </div>
  )
}
